import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import AnimatedTabButton from 'components/animations/AnimatedTabButton';
import NoData from 'components/NoData';
import { LabReportDetail } from 'services/interpret-lab-report/type';
import { PersonalisedAIReportDetail } from 'services/personalised-ai-report/type';
import ReportActions from './ReportActions';
import { downloadFile } from 'lib/utils';
import { Separator } from 'components/ui/separator';
import ReportInsights from './ReportInsigts';
import HTMLPreview from './HTMLPreview';
import InputData from './InputData';
import { useGetHealthReadingById } from 'services/face-scan/hooks';
import Loader from 'components/Loader';
import { ScrollArea } from 'components/ui/scroll-area';
import InputParameters from './InputParameters';
import { getCategorizedScanData } from 'lib/health-readings';
import ReportGenerating from 'components/reports/ReportGenerating';

interface ReportDetailInfoProps<T> {
  isError: boolean;
  reportDetail: LabReportDetail[] | PersonalisedAIReportDetail[] | undefined;
  tabs: {
    label: string;
    value: T;
  }[];
  isDeleting: boolean;
  onDelete: () => void;
  reportType?: 'lab' | 'ai';
  selectedTab: T;
  setSelectedTab: (tab: T) => void;
}

export default function ReportDetailInfo<T>({
  tabs,
  isError,
  reportDetail,
  isDeleting,
  onDelete,
  reportType = 'ai',
  selectedTab,
  setSelectedTab,
}: ReportDetailInfoProps<T>) {
  const { t } = useTranslation();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { data: healthReadings, isFetching: isReadingFetching } =
    useGetHealthReadingById({
      readingId: reportDetail?.[0]?.payload?.scan_data || '',
    });

  const categorizedScanData = useMemo(
    () => getCategorizedScanData({ healthReadings, selectedIndex: 0 }),
    [healthReadings]
  );

  const onDownload = async (url: string | undefined) => {
    setIsDownloading(true);
    try {
      await downloadFile(url);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isReadingFetching) {
    return <Loader />;
  }

  if (isError || !reportDetail?.length) {
    return (
      <div className='h-[calc(100vh-12rem)]'>
        <NoData className='border-none' message={t('no_report_data')} />
      </div>
    );
  }

  if (reportDetail[0]?.status === 'processed') {
    return <ReportGenerating isDetails reportType={reportType} />;
  }

  const reportActions = () => {
    return (
      <ReportActions
        content={reportDetail?.[0]?.report_link || ''}
        isDownloading={isDownloading}
        isDeleting={isDeleting}
        onDelete={onDelete}
        onDownload={() => onDownload(reportDetail?.[0]?.report_link)}
        disabled={!reportDetail?.length}
      />
    );
  };

  return (
    <div>
      <div className='hidden mb-2 lg:block xl:hidden'>{reportActions()}</div>

      <div className='flex items-center justify-between mb-2'>
        <div>
          {tabs.map(({ label, value }) => {
            if (
              value === 'insights' &&
              (!reportDetail?.[0]?.ai_insights ||
                !Object.keys(reportDetail?.[0]?.ai_insights)?.length)
            )
              return null;

            return (
              <AnimatedTabButton
                key={value as string}
                onClick={() => setSelectedTab(value)}
                isSelected={value === selectedTab}
                label={label}
              />
            );
          })}
        </div>

        <div className='hidden xl:block'>{reportActions()}</div>
      </div>

      <Separator className='w-full' />

      <AnimatePresence mode='wait'>
        <motion.div
          key={(selectedTab as string) || 'empty'}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {selectedTab === 'insights' && (
            <ReportInsights
              insight={reportDetail?.[0]?.ai_insights}
              scanId={reportDetail?.[0]?.payload?.scan_data || ''}
              reportType={reportType}
            />
          )}
          {['actual_report', 'generated_report'].includes(
            selectedTab as string
          ) && <HTMLPreview htmlString={reportDetail[0].ai_response} />}
          {selectedTab === 'input_data' && (
            <InputData
              scanData={categorizedScanData}
              questionnaireData={
                reportDetail?.[0]?.payload?.questionnaire || []
              }
              labReportLink={
                (reportDetail?.[0] as LabReportDetail)?.file_link ?? ''
              }
            />
          )}
          {selectedTab === 'input_parameters' && (
            <ScrollArea className='h-[calc(100vh-17rem)] lg:h-full'>
              <InputParameters
                scanData={categorizedScanData}
                questionnaireData={
                  reportDetail?.[0]?.payload?.questionnaire || []
                }
              />
            </ScrollArea>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
